interface ZakekeMessageCommon {
    readonly zakekeMessageType: number | string;
}

interface ZakekeMessageCustomizerAddToCart extends ZakekeMessageCommon {
    readonly zakekeMessageType: 0;
    readonly designId: string;
}

interface ZakekeMessageAttributeSelection {
    readonly Id: string | number;
    readonly Value: {
        readonly Id: string | number;
    };
}

interface ZakekeMessageCustomizerTestOrderRow {
    readonly attributes?: ReadonlyArray<ZakekeMessageAttributeSelection>;
    readonly quantity: number | string;
    readonly modificationID?: string | null;
}

interface ZakekeMessageCustomizerSpecializedAddToCart extends ZakekeMessageCommon {
    /**
     * 4 = legacy Bulk (AddToCartMultiple)
     * 6 = Name & Number (AddToCartNameAndNumber)
     * 8 = new Bulk (AddToCartBulkAsNN)
     */
    readonly zakekeMessageType: 4 | 6 | 8;
    readonly data: {
        readonly designID: string;
        readonly attributes: ReadonlyArray<ZakekeMessageCustomizerTestOrderRow>;
        readonly promiseId?: string | number | null;
    };
}

export interface CustomizerTestOrderDetail {
    readonly quantity: number;
    readonly designModificationID?: string;
    readonly attributes?: ReadonlyArray<{
        readonly id: string;
        readonly valueId: string;
    }>;
}

interface ZakekeMessageComposerAddToCart extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'AddToCart';
    readonly message: {
        readonly composition: string;
        readonly preview?: string | null;
        readonly quantity?: number;
        readonly attributes?: ReadonlyArray<unknown>;
        readonly designItems?: ReadonlyArray<unknown>;
        readonly additionalProperties?: Readonly<Record<string, unknown>>;
        readonly designPrice?: number;
        readonly design?: string | null;
    };
}

interface ZakekeMessageComposerPrice extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'Price';
    readonly messageId?: string | number | null;
    readonly message: {
        readonly attributes?: ReadonlyArray<unknown>;
        readonly compositionPrice?: number;
        readonly designPrice?: number;
    };
}

interface ZakekeMessageComposerSharedComposition extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'SharedComposition';
    readonly messageId?: string | number | null;
    readonly message: {
        readonly compositionDocID?: string | null;
    };
}

interface ZakekeMessageDesignChange extends ZakekeMessageCommon {
    readonly zakekeMessageType: 1;
    readonly design?: {
        readonly color?: string | null;
    };
}

interface ZakekeMessageCustomizerProductAttributes extends ZakekeMessageCommon {
    readonly zakekeMessageType: 2;
    readonly data?: {
        readonly promiseId?: string | number | null;
    };
}

interface ZakekeMessageCustomizerProductPrice extends ZakekeMessageCommon {
    readonly zakekeMessageType: 3;
    readonly data?: {
        readonly promiseId?: string | number | null;
    };
}

export interface ZakekeAddon {
    readonly addonID: number;
    readonly billingSystemAddonID: string | null;
    readonly description: string;
    readonly name: string;
    readonly periodUnit: 'DAY' | 'MONTH' | 'WEEK' | 'YEAR';
    readonly code: string;
    readonly published: boolean;
    readonly price: number;
    readonly sellerID: number | null;
    readonly rates: ReadonlyArray<{
        readonly feeValue: number;
    }>;
}

export interface ZakekeMessageAddonsList extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'AddonsList';
    readonly addons: ReadonlyArray<ZakekeAddon>;
    readonly currency: string;
}

// Iframe -> backoffice: the Test Mode iframe is ready to receive initialization messages.
interface ZakekeMessageTestModeReady extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'TestModeReady';
}

// Discovery mode channel (mirrors the customizer's useDiscoveryModeUpdater contract).
// Iframe -> backoffice: the customizer announces it has booted in discovery mode and is listening.
interface ZakekeMessageDiscoveryModeReady extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeReady';
}

// Iframe -> backoffice: the customizer is fully loaded and ready for interaction — booted, and (if a
// variant-selection dialog was prompted) the shopper has chosen a variant so the dialog is gone. Flips
// back to false if a variant dialog reopens (e.g. an out-of-stock variant change). Drives the discovery
// tool buttons' enabled state — they stay disabled until this is true.
interface ZakekeMessageDiscoveryModeInteractive extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeInteractive';
    readonly interactive: boolean;
}

// Iframe -> backoffice: the ids of AI Creative Tools / Tools discovery pills that are already active on the
// opened product (computed from the product's real, non-discovery configuration, aggregated across all
// variants/sides). The panel hides these pills — there's nothing to "discover" about a feature the merchant
// already has. Snapshotted once the customizer is interactive; recomputed only when the product changes.
interface ZakekeMessageDiscoveryModeHiddenTools extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeHiddenTools';
    readonly hiddenTools: string[];
}

// Iframe -> backoffice: the merchant clicked "enable this on my product" inside a discovery tool's dialog
// (e.g. the Get a Quote form's "Not available in Test Mode" dialog, or an explainer-only tool's dialog). The
// host navigates to that tool's real setup page (product pre-assigned via ?fromProductId where that page reads
// it), exactly like the panel's Activate button.
interface ZakekeMessageDiscoveryModeActivateTool extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeActivateTool';
    readonly tool: string;
}

// Backoffice -> iframe: stream a feature selection (print method / pricing rule / AI / tool / commerce rule)
// to apply to the previewed product without reloading. Payload shape is finalized in a later step.
export interface ZakekeMessageDiscoveryModeUpdate extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeUpdate';
    readonly category: 'printing' | 'pricing' | 'ai' | 'tools' | 'commerce' | 'quantity';
    readonly payload: unknown;
}

// Iframe -> backoffice: the customizer just completed a real AI generation in discovery mode (one credit
// consumed). The host re-resolves the shared GENERATIVE_AI balance and, if it is now exhausted, shows the
// "out of credits" warning -> buy-credits flow. Carries the discovery tool id for labelling.
interface ZakekeMessageDiscoveryAiGenerationCompleted extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryAiGenerationCompleted';
    readonly tool?: string;
}

// Backoffice -> iframe: tell the customizer to re-read its AI-credit availability (servicesEnabled +
// imageGenerationSettings) after a discovery credit dialog granted the trial or bought a pack, so the
// just-unlocked AI tools work without a full reload. Mirrors the customizer's DiscoveryModeRefreshCredits
// handler (zakeke-customizer-react/useDiscoveryModeUpdater).
export interface ZakekeMessageDiscoveryModeRefreshCredits extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeRefreshCredits';
}

export type ZakekeMessage =
    | ZakekeMessageCommon
    | ZakekeMessageCustomizerAddToCart
    | ZakekeMessageCustomizerSpecializedAddToCart
    | ZakekeMessageComposerAddToCart
    | ZakekeMessageComposerPrice
    | ZakekeMessageComposerSharedComposition
    | ZakekeMessageDesignChange
    | ZakekeMessageCustomizerProductAttributes
    | ZakekeMessageCustomizerProductPrice
    | ZakekeMessageAddonsList
    | ZakekeMessageTestModeReady
    | ZakekeMessageDiscoveryModeReady
    | ZakekeMessageDiscoveryModeInteractive
    | ZakekeMessageDiscoveryModeHiddenTools
    | ZakekeMessageDiscoveryModeActivateTool
    | ZakekeMessageDiscoveryAiGenerationCompleted
    | ZakekeMessageDiscoveryModeRefreshCredits
    | ZakekeMessageDiscoveryModeUpdate;

export const postAddonsList = (iframe: HTMLIFrameElement | null, addons: ReadonlyArray<ZakekeAddon>, currency: string) => {
    const message: ZakekeMessageAddonsList = {
        zakekeMessageType: 'AddonsList',
        addons,
        currency
    };
    iframe?.contentWindow?.postMessage(message, '*');
};

export const isCustomizerAddToCartMessage = (message: any): message is ZakekeMessageCustomizerAddToCart => message?.zakekeMessageType === 0;

export const isCustomizerSpecializedAddToCartMessage = (message: any): message is ZakekeMessageCustomizerSpecializedAddToCart =>
    (message?.zakekeMessageType === 4 || message?.zakekeMessageType === 6 || message?.zakekeMessageType === 8) &&
    typeof message?.data?.designID === 'string' &&
    message.data.designID.length > 0 &&
    Array.isArray(message?.data?.attributes);

/**
 * Converts the specialized customizer payload into the OrderService mutation input.
 * Name & Number and BulkAsNN are resolved server-side through modificationID; only
 * legacy Bulk forwards the selected attributes because it has no design modifications.
 */
export const getCustomizerTestOrderDetails = (message: ZakekeMessageCustomizerSpecializedAddToCart): ReadonlyArray<CustomizerTestOrderDetail> | null => {
    const usesDesignModifications = message.zakekeMessageType === 6 || message.zakekeMessageType === 8;
    const details: CustomizerTestOrderDetail[] = [];

    for (const row of message.data.attributes) {
        const quantity = Number(row.quantity);
        if (!Number.isInteger(quantity) || quantity <= 0) return null;

        if (usesDesignModifications) {
            const designModificationID = typeof row.modificationID === 'string' ? row.modificationID.trim() : '';
            if (!designModificationID) return null;

            details.push({ quantity, designModificationID });
            continue;
        }

        if (!Array.isArray(row.attributes) || row.attributes.length === 0) return null;

        const attributes: Array<{ id: string; valueId: string }> = [];
        for (const attribute of row.attributes) {
            if (attribute?.Id == null || attribute?.Value?.Id == null) return null;

            const id = String(attribute.Id);
            const valueId = String(attribute.Value.Id);
            if (!id || !valueId) return null;

            attributes.push({ id, valueId });
        }

        details.push({ quantity, attributes });
    }

    return details.length > 0 ? details : null;
};

export const isComposerAddToCartMessage = (message: any): message is ZakekeMessageComposerAddToCart =>
    message?.zakekeMessageType === 'AddToCart' && typeof message?.message?.composition === 'string' && message.message.composition.length > 0;

export const isComposerPriceMessage = (message: any): message is ZakekeMessageComposerPrice => message?.zakekeMessageType === 'Price' && !!message?.message;

export const isComposerSharedCompositionMessage = (message: any): message is ZakekeMessageComposerSharedComposition =>
    message?.zakekeMessageType === 'SharedComposition' && !!message?.message;

/** @deprecated Use isCustomizerAddToCartMessage instead */
export const isAddToCartMessage = isCustomizerAddToCartMessage;

export const isDesignChangeMessage = (message: ZakekeMessage): message is ZakekeMessageDesignChange => message.zakekeMessageType === 1;

export const isCustomizerProductAttributesMessage = (message: ZakekeMessage): message is ZakekeMessageCustomizerProductAttributes =>
    message.zakekeMessageType === 2;

export const isCustomizerProductPriceMessage = (message: ZakekeMessage): message is ZakekeMessageCustomizerProductPrice => message.zakekeMessageType === 3;

export const isDiscoveryModeReadyMessage = (message: any): message is ZakekeMessageDiscoveryModeReady => message?.zakekeMessageType === 'DiscoveryModeReady';

export const isTestModeReadyMessage = (message: any): message is ZakekeMessageTestModeReady => message?.zakekeMessageType === 'TestModeReady';

export const isDiscoveryModeInteractiveMessage = (message: any): message is ZakekeMessageDiscoveryModeInteractive =>
    message?.zakekeMessageType === 'DiscoveryModeInteractive' && typeof message?.interactive === 'boolean';

export const isDiscoveryModeHiddenToolsMessage = (message: any): message is ZakekeMessageDiscoveryModeHiddenTools =>
    message?.zakekeMessageType === 'DiscoveryModeHiddenTools' && Array.isArray(message?.hiddenTools);

export const isDiscoveryModeActivateToolMessage = (message: any): message is ZakekeMessageDiscoveryModeActivateTool =>
    message?.zakekeMessageType === 'DiscoveryModeActivateTool' && typeof message?.tool === 'string';

export const isDiscoveryAiGenerationCompletedMessage = (message: any): message is ZakekeMessageDiscoveryAiGenerationCompleted =>
    message?.zakekeMessageType === 'DiscoveryAiGenerationCompleted';
